import { useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@zg-rentals/react-query5';
import { Anchor, Box, Button, Heading, Paragraph, TextButton } from '@zillow/constellation-10';
import Image from 'next/image';
import { Divider } from '@zillow/constellation-10';
import {
  SatelliteWebRenterConversationHandler_getLatestConversations,
  SatelliteWebRenterConversationHandler_getLatestConversations_Path,
} from '@zg-rentals/ts-satellite-client';
import { SatelliteConversationFolderTypeV2 } from '@zg-rentals/ts-satellite-client';
import type { UpdateConversationStateArgs } from '../../types/UpdateConversationStateArgs';
import ConfigConstants from '../../constants/ConfigConstants';
import StringConstants from '../../constants/StringConstants';
import { isZillow } from '@zg-rentals/multibrand-support';
import { AppContext } from '../../contexts/AppContextProvider';
import type { User } from '../../AuthProvider';
import EnvelopeEmptyIcon from '../../images/iconography/envelope-empty.svg';
import {
  emitEmptyStateWithFindYourRentalButtonEvent,
  emitRedirectToRentalsSearchPageEvent,
  emitSendToZrmInboxEvent,
  EMPTY_STATE_WITH_FIND_YOUR_RENTAL_BUTTON_EVENT_TYPE_ID,
  REDIRECT_TO_RENTALS_SEARCH_PAGE_EVENT_TYPE_ID,
  SEND_TO_ZRM_INBOX_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/inboxIA';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import { logError } from '@zg-rentals/log-error';
import { bootstrap } from '../../bootstrap';
import NoConversationsErrorExperience from '../NoConversationsErrorExperience';
import { useRouter } from 'next/router';
import HpxInlineIcon from '../../images/iconography/hpx-inline.svg';
import { contentWrapperStyles, iconCommentInverseStyles } from './styles';

const logger = bootstrap.logger.child({ name: 'NoConversationsContent' });

const { GRAPHQL_ERROR_TYPES } = StringConstants;

type Props = {
  user: User;
  errorCode?: string;
  showErrorExperience?: boolean;
  updateConversationState?(args: UpdateConversationStateArgs): void;
  onLoadMoreClick?(): void;
  /** When true, page has already loaded older (660d) and got 0 conversations — show "No messages yet" even if local state would show Load older (e.g. after remount). */
  hasLoadedOlderWithZeroResults?: boolean;
};

export default function NoConversationsContent({
  user,
  errorCode,
  showErrorExperience = false,
  updateConversationState,
  onLoadMoreClick,
  hasLoadedOlderWithZeroResults = false,
}: Props): JSX.Element {
  const {
    query: { utm_source, utm_medium },
  } = useRouter();
  const isComingFromSms = utm_source === 'sms';
  const isComingFromEmail = utm_medium === 'email';
  const { brand } = useContext(AppContext);
  const { folder } = useContext(InboxPageContext);
  const queryClient = useQueryClient();
  const [showLoadOlderConversationsButton, setShowLoadOlderConversationsButton] = useState(true);
  const [loadMoreConversationsErrorMessage, setLoadMoreConversationsErrorMessage] = useState('');

  const isNotAuthorized = errorCode === GRAPHQL_ERROR_TYPES.AUTHORIZATION;
  const isZillowBrand = isZillow(brand);

  useEffect(() => {
    setShowLoadOlderConversationsButton(true);
  }, [folder]);

  useEffect(() => {
    if (isZillowBrand && !showErrorExperience && !showLoadOlderConversationsButton) {
      emitEmptyStateWithFindYourRentalButtonEvent(user);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [isZillowBrand, showErrorExperience, showLoadOlderConversationsButton]);

  const currentFolderDisplayText = useMemo((): string => {
    if (folder === SatelliteConversationFolderTypeV2.ARCHIVED) {
      // UI should show Archive instead of Archived (no 'd')
      // but the BE, Graphql server, everything, uses term "archived"
      return 'archive';
    } else if (folder === SatelliteConversationFolderTypeV2.INBOX) {
      return isZillowBrand ? 'recent' : 'inbox';
    }

    return folder.toLowerCase();
  }, [folder, isZillowBrand]);

  const showNoMessagesYet = !showLoadOlderConversationsButton || !!hasLoadedOlderWithZeroResults;

  const handleLoadOlderConversationsClick = async (): Promise<void> => {
    const postData = {
      folder,
      daysBack: ConfigConstants.MAX_DAYS_BACK_FOR_CONVERSATIONS,
    };

    try {
      logger.info(postData, 'User clicked to load older conversations, getting latest conversations to redirect to');

      // Use a different query key so we don't overwrite the infinite-query cache (which expects
      // InfiniteData shape { pages, pageParams }). Otherwise TanStack reads .pages.length and crashes.
      const data = await queryClient.fetchQuery({
        queryKey: [SatelliteWebRenterConversationHandler_getLatestConversations_Path, postData, 'loadOlderOneOff'],
        queryFn: () => SatelliteWebRenterConversationHandler_getLatestConversations(postData),
      });

      const conversations = data?.conversations;

      if (conversations?.length && updateConversationState) {
        const latestConversationId = conversations[0].conversationId;
        return updateConversationState({
          conversationId: latestConversationId!,
          hasLoadedOlderConversations: true,
        });
      }
      setShowLoadOlderConversationsButton(false);
      if (updateConversationState) {
        updateConversationState({ conversationId: '', hasLoadedOlderConversations: true });
      }
    } catch (error) {
      logError({ error, context: 'Error fetching latest conversations on load older conversations click' });
      setLoadMoreConversationsErrorMessage(StringConstants.UNABLE_LOAD_OLD_CONVERSATIONS);
    }
  };

  const handleRedirectToZrmInbox = (): void => {
    emitSendToZrmInboxEvent('inbox_empty_state', user);
    window.location.href = '/rental-manager/inbox';
  };

  const handleRedirectToRentalsSearchPage = (): void => {
    emitRedirectToRentalsSearchPageEvent();
  };

  const zillowBrandNoConversationsContentComponent = (
    <Box
      css={contentWrapperStyles}
      data-eventid={EMPTY_STATE_WITH_FIND_YOUR_RENTAL_BUTTON_EVENT_TYPE_ID}
      date-eventdescription="View empty state"
    >
      <Box aria-hidden="true" css={{ marginBottom: 'default' }}>
        <Image src={EnvelopeEmptyIcon} width={120} height={120} alt="" />
      </Box>
      {showErrorExperience ? (
        <NoConversationsErrorExperience
          isNotAuthorized={isNotAuthorized}
          isComingFromSms={isComingFromSms}
          isComingFromEmail={isComingFromEmail}
          userEmail={user.email || undefined}
        />
      ) : (
        <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Heading level={3} textStyle="heading-xs" css={{ marginBottom: 'tightest' }}>
            {showNoMessagesYet ? `No messages yet` : `You have no recent conversations`}
          </Heading>
          {showNoMessagesYet && (
            <Paragraph textStyle="body" css={{ marginBottom: 'default', maxWidth: '357px' }}>
              Once you reach out to a rental manager, your conversations, including tour and application requests, will
              appear here.
            </Paragraph>
          )}
          {!showNoMessagesYet ? (
            <Box css={{ marginBottom: 'looser' }}>
              <Anchor asChild>
                <TextButton
                  onClick={onLoadMoreClick ? onLoadMoreClick : handleLoadOlderConversationsClick}
                  aria-label="load conversations older than 90 days"
                  tone="brand"
                  textStyle="body-bold"
                >
                  Load conversations older than 90 days
                </TextButton>
              </Anchor>
            </Box>
          ) : (
            <Button
              asChild
              tone="brand"
              onClick={handleRedirectToRentalsSearchPage}
              emphasis="filled"
              data-eventid={REDIRECT_TO_RENTALS_SEARCH_PAGE_EVENT_TYPE_ID}
              css={{
                marginLeft: 'auto',
                marginRight: 'auto',
                marginBottom: 'looser',
                marginTop: 'default',
              }}
            >
              <a href={'/homes/for_rent'}>Search for rentals</a>
            </Button>
          )}
          {!!loadMoreConversationsErrorMessage && (
            <Paragraph textStyle="body-sm" css={{ marginBottom: 'looser', color: 'text.feedback.critical.hero' }}>
              {loadMoreConversationsErrorMessage}
            </Paragraph>
          )}
          <Divider css={{ marginTop: 'tighter', width: '330px' }} />
          <Paragraph textStyle="body" css={{ marginTop: 'loose' }}>
            Are you a property manager?
          </Paragraph>
          <Paragraph textStyle="body">
            Visit{' '}
            <Anchor asChild>
              <TextButton
                tone="brand"
                textStyle="body-bold"
                onClick={handleRedirectToZrmInbox}
                data-eventid={SEND_TO_ZRM_INBOX_EVENT_TYPE_ID}
              >
                Zillow Rental Manager
              </TextButton>
            </Anchor>
          </Paragraph>
        </Box>
      )}
    </Box>
  );

  if (isZillowBrand) {
    return zillowBrandNoConversationsContentComponent;
  }

  return (
    <Box css={contentWrapperStyles}>
      <Box aria-hidden="true" css={{ marginBottom: 'default' }}>
        <Box css={iconCommentInverseStyles}>
          <Image src={HpxInlineIcon} alt="" width={44} height={44} />
        </Box>
        <Image src={HpxInlineIcon} alt="" width={44} height={44} />
      </Box>

      {showErrorExperience ? (
        <NoConversationsErrorExperience
          isNotAuthorized={isNotAuthorized}
          isComingFromSms={isComingFromSms}
          isComingFromEmail={isComingFromEmail}
          userEmail={user.email || undefined}
        />
      ) : (
        <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Paragraph textStyle="body-sm" css={{ marginBottom: 'tighter' }}>
            {`No messages in your ${currentFolderDisplayText} to show.`}
          </Paragraph>
          {!showNoMessagesYet && (
            <Heading level={5} textStyle="body-lg-bold">
              <Anchor asChild>
                <TextButton
                  onClick={onLoadMoreClick ? onLoadMoreClick : handleLoadOlderConversationsClick}
                  aria-label="load conversations older than 90 days"
                >
                  Load conversations older than 90 days
                </TextButton>
              </Anchor>
            </Heading>
          )}
          {!!loadMoreConversationsErrorMessage && (
            <Paragraph textStyle="body-sm" css={{ color: 'brand' }}>
              {loadMoreConversationsErrorMessage}
            </Paragraph>
          )}
        </Box>
      )}
    </Box>
  );
}
